import { FC } from 'react'
import { Chip } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'

import { useDarkMode } from '../contexts/DarkModeContext'
import { hasPendingSubmission } from '../utils/helpers/agami.helpers'

interface AgamiDraftChipProps {
  spot: {
    is_draft: boolean
    isProposal?: boolean
    experience_draft_submissions?: Array<{ outcome: string }>
  }
}

/**
 * The badge of an unpublished agami (05.3/05.6), shared between the cards and
 * the table so the two views cannot diverge on what a draft looks like.
 *
 * Two natures, one component: a WORKING DRAFT is the owner's own reminder, a
 * PROPOSAL is a queue generated by someone else and awaits a decision — the
 * discriminant is a pending submission in the satellite (D15), which RLS makes
 * visible to the owner only. A published agami gets no chip at all.
 */
const AgamiDraftChip: FC<AgamiDraftChipProps> = ({ spot }) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()

  if (!spot.is_draft) return null

  const isProposal = spot.isProposal ?? hasPendingSubmission(spot)

  return (
    <Chip
      value={t(isProposal ? 'agami.proposal' : 'agami.draft')}
      size="sm"
      color={isProposal ? 'amber' : 'blue'}
      variant={darkMode ? 'filled' : 'ghost'}
      className="inline-block"
      data-testclass={isProposal ? 'agami-proposal-chip' : 'agami-draft-chip'}
    />
  )
}

export default AgamiDraftChip
